import { useLayoutEffect, type MutableRefObject } from 'react';

// iOS Safari doesn't focus a button when it is tapped, so focus is often still
// on the page (or a sheet) when a dialog opens. Note the last control touched
// (pointerdown too, for a long press) to stand in for it.
let lastTapped: { el: HTMLElement; at: number } | null = null;
if (typeof document !== 'undefined') {
	const note = (event: Event) => {
		const el = (event.target as Element | null)?.closest?.('button, a[href], [role="button"], [tabindex]');
		lastTapped = el instanceof HTMLElement ? { el, at: performance.now() } : null;
	};
	document.addEventListener('pointerdown', note, true);
	document.addEventListener('click', note, true);
}

// Sheets that are open, mapped to what opened them. A row in an action sheet
// can open a dialog as the sheet closes; that dialog then hands focus back to
// the sheet's opener (a "…" button) rather than to a row that is going away.
const sheetOpeners = new WeakMap<Element, MutableRefObject<HTMLElement | null>>();

/** Pass a sheet's content node so dialogs opened from inside it skip past it. */
export function registerSheetOpener(node: Element | null, opener: MutableRefObject<HTMLElement | null>) {
	if (node) sheetOpeners.set(node, opener);
}

function currentOpener(): HTMLElement | null {
	const active = document.activeElement;
	const tapped = lastTapped?.el.isConnected && performance.now() - lastTapped.at < 1000 ? lastTapped.el : null;
	let el = tapped ?? (active instanceof HTMLElement && active !== document.body ? active : null);
	// A few levels at most: a dialog from a sheet opened from another sheet.
	for (let depth = 0; el && depth < 4; depth++) {
		const sheet = el.closest('[data-opener-sheet]');
		const sheetOpener = sheet ? sheetOpeners.get(sheet)?.current : null;
		if (!sheet || !sheetOpener || sheet.contains(sheetOpener)) break;
		el = sheetOpener;
	}
	return el;
}

/**
 * Notes what had focus when a sheet or dialog opened, so closing it can hand
 * focus back. Render it as the content's first child: its effect then runs
 * before a field inside takes focus with autoFocus, which also stops Radix's
 * onOpenAutoFocus from firing at all.
 */
export function RecordOpener({ into }: { into: MutableRefObject<HTMLElement | null> }) {
	useLayoutEffect(() => {
		into.current = currentOpener();
	}, [into]);
	return null;
}
